import {
  AutocompleteInput,
  ConfigurationContext,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FormGetter,
  FormInput,
  HasuraDataTableColumnDef,
  SelectInput,
} from '@kirz/mui-admin';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export const FEATURED_TEMPLATE_TYPE_ITEMS = [
  { value: 'video', text: 'Видео' },
  { value: 'image', text: 'Изображение' },
];

export const FeaturedTemplatesTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);
    const [sortValues, setSortValues] = useState<number[] | null>(null);
    const { hasura } = useContext(ConfigurationContext);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const refreshSortValues = async () => {
      const items = await hasura.request({
        type: 'query',
        source: 'featuredTemplate',
        selection: 'sort',
      });
      setSortValues(items.map((x: any) => x.sort));
    };

    useEffect(() => {
      refreshSortValues();
    }, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () => [
        {
          field: 'sort',
          width: 60,
          type: 'sort',
          sortKey: 'sort',
          rowsSortValues: sortValues,
          onSortChange: async (direction, row, targetRowSort) => {
            const source = 'FeaturedTemplate';
            const { id } = row;

            await hasura.request({
              type: 'custom',
              query: `
                mutation ChangeSort($where1: ${source}BoolExp!, $set1: ${source}SetInput!, $where2: ${source}BoolExp!, $set2: ${source}SetInput!) {
                  result2: update${source}(where: $where2, _set: $set2) {
                    affected_rows
                  }
                  result1: update${source}(where: $where1, _set: $set1) {
                    affected_rows
                  }
                }
              `,
              variables: {
                where1: { id: { _eq: id } },
                set1: { sort: targetRowSort },
                where2: {
                  sort: { _eq: targetRowSort },
                },
                set2: { sort: row.sort },
              },
            });

            tableRef.current?.reload();
          },
        },
        {
          field: 'text',
          headerName: 'Текст',
          flex: 1,
        },
        {
          field: 'description',
          headerName: 'Описание',
          flex: 1,
        },
        {
          field: 'type',
          headerName: 'Тип',
          width: 120,
          type: 'select',
          items: FEATURED_TEMPLATE_TYPE_ITEMS,
        },
        {
          field: 'template',
          selector: 'template { id name }',
          headerName: 'Шаблон',
          flex: 1,
          valueGetter: ({ row }) => row.template?.name,
          sortable: false,
        },
        {
          field: 'createdAt',
          headerName: 'Дата создания',
          type: 'date',
          width: 180,
        },
      ],
      [sortValues],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="featuredTemplate"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'sort', sort: 'asc' }}
        formTitle={(isNew) => (isNew ? 'Новый слайд' : 'Редактировать слайд')}
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'sm',
          formSubmitterProps: {
            onSubmit(item) {
              refreshSortValues();
            },
          },
          formProps: {
            defaultValues: {
              sort: 0,
              type: 'video',
            },
          },
        }}
        deletable={{
          onDeleted: (row) => {
            setSortValues(sortValues!.filter((x) => x !== row.sort));
          },
        }}
      >
        <FormInput name="text" label="Текст" required />
        <FormInput name="description" label="Описание" required />
        <SelectInput
          name="type"
          label="Тип"
          required
          items={FEATURED_TEMPLATE_TYPE_ITEMS}
        />

        <FormGetter
          names={['type']}
          render={({ type }) => (
            <>
              <AutocompleteInput
                name="templateId"
                label="Шаблон"
                source="template"
                mode="hasura"
                selection="id name"
                itemText="name"
                itemValue="id"
                required
                filter={{
                  type: { _eq: type },
                }}
              />
            </>
          )}
        />
      </DataTableEx>
    );
  },
);
